import { createContext, ReactNode, useContext, useMemo } from 'react'
import useSWR from 'swr'
import { getPrerequisites } from 'api/learning-paths/get'
import { ICourse, IPrerequisites } from 'models/prerequisites.model'

interface PrerequisitesContextType {
  isLoading: boolean
  isPending: boolean
  courses: ICourse[]
  learningPaths: IPrerequisites[]
}

export const PrerequisitesContext = createContext<PrerequisitesContextType>({
  isLoading: true,
  isPending: false,
  courses: [],
  learningPaths: [],
})

interface UsePrerequisitesProps {
  courseId: number
  children: ReactNode
}

export const PrerequisitesProvider = ({
  courseId,
  children,
}: UsePrerequisitesProps) => {
  const { data, isLoading } = useSWR(
    { key: 'prerequisites', courseId },
    async (args) => getPrerequisites({ courseId: args.courseId })
  )
  const { canContinueCourse = true } = { ...data }
  const learningPaths = data?.data
  const isPending = !canContinueCourse

  const courses = useMemo(() => {
    if (!learningPaths) return []

    const list: ICourse[] = learningPaths.flatMap((item) => [...item.courses])
    const mapUniqCourses = new Map(list.map((item) => [item.course.id, item]))
    return Array.from(mapUniqCourses.values())
  }, [learningPaths])

  return (
    <PrerequisitesContext.Provider
      value={{
        isLoading,
        isPending,
        courses,
        learningPaths: learningPaths || [],
      }}
    >
      {children}
    </PrerequisitesContext.Provider>
  )
}

export const usePrerequisites = () => useContext(PrerequisitesContext)
